import { Localisation, ILocalisationLibelle } from './localisation';
import { ITypeChamp, TypeChamp } from './typeChamp';
import { SearchFilter } from '../../shared/classes/search-filter';
import { SearchFilterConfiguration, Searchable } from './searchFilterConfiguration';

export interface IChamp {
    id?: string;
    entiteId: string;
    type: TypeChamp;
    code: string;
    requis?: boolean;
    actif?: boolean;
    listeOrdre?: number;
    listeActif?: boolean;
    langues: Array<IChampLibelle>;
    champValeurs?: Array<IChampValeur>;
    recherche: boolean;
    rechercheRequis: boolean;

    champOngletOrdre?: number;
    champOngletColonne?: number;
    champOngletSection?: string;
}

export interface IChampLibelle extends ILocalisationLibelle {
    champId?: string;
}

export interface IChampValeur {
    id?: string;
    champId?: string;
    langueId: string;
    valeurs: {[field: string]: string} | {value: string, couleur: string};
}

export class Champ extends Localisation<IChampLibelle> implements IChamp, Searchable {

    id?: string;
    entiteId: string;
    type: TypeChamp;
    code: string;
    actif: boolean;
    requis: boolean;
    listeOrdre?: number;
    listeActif: boolean;
    champValeurs: Array<IChampValeur>;

    champOngletOrdre?: number;
    champOngletColonne?: number;
    champOngletSection?: string;

    recherche: boolean;
    rechercheRequis: boolean;

    searchFilterConfiguration: SearchFilterConfiguration;

    constructor(champ: IChamp) {
        super(champ.langues);

        this.id = champ.id;
        this.entiteId = champ.entiteId;
        this.type = champ.type;
        this.code = champ.code;
        this.actif = champ.actif;
        this.requis = champ.requis;
        this.listeOrdre = champ.listeOrdre;
        this.listeActif = champ.listeActif;
        this.champValeurs = champ.champValeurs;

        this.champOngletOrdre = champ.champOngletOrdre;
        this.champOngletColonne = champ.champOngletColonne;
        this.champOngletSection = champ.champOngletSection;

        this.rechercheRequis = champ.rechercheRequis;

        this.recherche = champ.recherche;
    }

    static fromJson(champ: any): Champ {
        return new Champ({
            id: champ.chp_id,
            entiteId: champ.chp_ent_id,
            type: TypeChamp.fromJson(champ.chp_typ),
            code: champ.chp_code,
            actif: champ.chp_actif,
            requis: (champ.chp_requis && champ.chp_requis === '1' ? true : false),
            listeOrdre: champ.chp_lst_ordre,
            listeActif: champ.chp_lst_actif,
            langues: champ.langues.map(langue => {
                return {
                    id: langue.chl_id,
                    champId: langue.chl_chp_id,
                    langueId: langue.chl_lng_id,
                    libelle: langue.chl_libelle
                };
            }),
            champValeurs: champ.champ_valeurs ? champ.champ_valeurs.map(cv => {
                return {
                    id: cv.chv_id,
                    champId: cv.chv_chp_id,
                    langueId: cv.chv_lng_id,
                    valeurs: cv.chv_valeur,
                };
            }) : [],

            champOngletOrdre: champ.cho_ordre,
            champOngletColonne: champ.cho_colonne,
            champOngletSection: champ.cho_sec_id,
            recherche: champ.chp_recherche,
            rechercheRequis: (champ.chp_recherche_requis && champ.chp_recherche_requis === '1' ? true : false),
        });
    }

    toJson() {
        return {
            chp_id: this.id,
            chp_ent_id: this.entiteId,
            chp_typ_id: this.type.id,
            chp_code: this.code,
            chp_actif: this.actif,
            chp_requis: this.requis,
            chp_lst_ordre: this.listeOrdre,
            chp_lst_actif: this.listeActif,
            chp_recherche: this.recherche,
            langues: this.langues.map(langue => {
                return {
                    chl_id: langue.id,
                    chl_lng_id: langue.langueId,
                    chl_libelle: langue.libelle
                };
            }),
            champ_valeurs: this.champValeurs.map(cv => {
                return {
                    chv_id: cv.id,
                    chv_chp_id: this.id,
                    chv_lng_id: cv.langueId,
                    chv_valeur: cv.valeurs
                };
            }),

            cho_ordre: this.champOngletOrdre,
            cho_colonne: this.champOngletColonne,
            cho_sec_id: this.champOngletSection,
            chp_recherche_requis: this.rechercheRequis,
        };
    }

    toChampOngletJson() {
        return {
            chp_id: this.id,
            cho_ordre: this.champOngletOrdre,
            cho_colonne: this.champOngletColonne,
        };
    }

    toChampOngletSectionJson() {
        return {
            chp_id: this.id,
            cho_ordre: this.champOngletOrdre,
            cho_colonne: this.champOngletColonne,
            cho_sec_id: this.champOngletSection
        };
    }

    getSearchFilterConfiguration(): SearchFilterConfiguration {
        if (this.searchFilterConfiguration == null) {
            this.searchFilterConfiguration = new SearchFilterConfiguration(this.code, this.type.code, true);
        }

        return this.searchFilterConfiguration;
    }

    getSearchFilter(): SearchFilter {
        return this.getSearchFilterConfiguration().searchFilter;
    }

    getChampValeurs(langueId: string) {
        return this.champValeurs.find(cv => cv.langueId === langueId);
    }

    getFormattedInputData(langueId: string, value: string, code = '') {
        const champValeurs = this.getChampValeurs(langueId);
        const options = champValeurs != null ? champValeurs.valeurs : [];

        return {
            id: code,
            label: this.getLibelle(langueId),
            value: value,
            canSelectMultipleValues: this.type.canHaveMultipleValues,
            options: options
        };
    }
}

